import { z } from "zod";

/** Eight megabytes: a modern phone photo, not a video. */
export const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

/**
 * UPLOADS NEVER GO THROUGH THE SERVICE.
 *
 * The service signs a URL, the phone uploads straight to storage, and only the resulting
 * key comes back. Anything else means a 20 MB photo travels twice, holds a Node process
 * open on a hotel Wi-Fi for a minute, and gives the container's memory limit a reason to
 * kill it — on the box that also runs everyone else's site.
 */
export const uploadRequestSchema = z.object({
  /**
   * The formats a phone camera actually produces, as an ENUM rather than a shape check.
   *
   * This lived as a Set inside the route, which meant the app could not reject a
   * screen-recording or a PDF before spending the upload round trip — on a train, several
   * seconds to be told no. A contract is shared precisely so the client can refuse first;
   * an allowlist that only the server knows contradicts the reason it is shared at all.
   */
  mimeType: z.enum(["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"]),
  /** Bytes. Checked against the ceiling before a URL is signed, not after the upload. */
  /**
   * The ceiling is in the CONTRACT, not in the route, for the same reason the format is: the
   * app can refuse an 18-megapixel HEIC before spending the upload round trip. It was a
   * hand-written `if` after the storage-configuration check, which also meant an oversized
   * file in an environment with no storage was told "storage is not configured".
   */
  size: z.number().int().positive().max(MAX_IMAGE_BYTES),
  purpose: z.enum(["note-image", "avatar"]),
});

export const uploadTicketSchema = z.object({
  /** PUT the bytes here, with the exact Content-Type that was asked for. */
  uploadUrl: z.url(),
  /** The only identifier the app should ever store or send back. */
  key: z.string(),
  /** Signed link for reading it back. Short-lived — re-fetch rather than caching it. */
  readUrl: z.url(),
  expiresAt: z.iso.datetime(),
});

/** The storage key in `/v1/media/:key/url`. Shared for the same reason as the note id. */
export const mediaKeySchema = z.object({ key: z.string().min(1) });

export type MediaKeyParam = z.infer<typeof mediaKeySchema>;
export type UploadRequest = z.infer<typeof uploadRequestSchema>;
export type UploadTicket = z.infer<typeof uploadTicketSchema>;

/** A freshly signed link for one stored object. Short-lived — re-fetch rather than cache. */
export const readUrlSchema = z.object({ readUrl: z.url() });

export type ReadUrl = z.infer<typeof readUrlSchema>;

